import { useState, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { useToast } from '@/components/ui/toast';

import {
  accessErrorText,
  CAPABILITIES,
  CAPABILITY_LABELS,
  localRuleError,
  type AccessRole,
  type Capability,
  type Rules,
  type TypeAccess,
} from '../document-access';
import { useSaveTypeAccess } from '../use-document-access';

interface Props {
  type: TypeAccess;
  roles: readonly AccessRole[];
  onClose: () => void;
}

/** Reglas de un tipo de documento: por acción, los roles que la tienen en ese tipo. Sin
 *  roles marcados, la acción sigue el permiso de cada rol (spec 2026-10-14 §8 D2). */
export function TypeAccessModal({ type, roles, onClose }: Props): ReactElement {
  const save = useSaveTypeAccess();
  const { showToast } = useToast();
  const [rules, setRules] = useState<Rules>(type.rules);
  const [serverError, setServerError] = useState<string | null>(null);
  const byId = new Map(roles.map((r) => [r.id, r]));
  const localError = localRuleError(rules, byId);
  const error = localError ?? serverError;
  const busy = save.isPending;

  function toggle(capability: Capability, roleId: string): void {
    setServerError(null);
    setRules((prev) => {
      const current = prev[capability];
      const next = current.includes(roleId)
        ? current.filter((id) => id !== roleId)
        : [...current, roleId];
      return { ...prev, [capability]: next };
    });
  }

  function submit(): void {
    if (busy || localError !== null) {
      return;
    }
    save.mutate(
      { typeId: type.id, rules },
      {
        onSuccess: () => {
          showToast(`Acceso de ${type.name} actualizado`);
          onClose();
        },
        // El diálogo queda abierto con lo marcado: se corrige o se reintenta.
        onError: (e) => setServerError(accessErrorText(e, byId)),
      },
    );
  }

  return (
    <ModalShell
      open
      title={`Acceso a ${type.name}`}
      wide
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn sm"
            onClick={submit}
            disabled={busy || localError !== null}
          >
            {busy ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <p className="soft" style={{ fontSize: 12.5, margin: '0 0 12px' }}>
        Marca los roles que tienen cada acción en este tipo. Sin roles marcados, la acción sigue el
        permiso de cada rol.
      </p>
      <p className="fld-hint" style={{ margin: '0 0 12px' }}>
        El Administrador de Calidad conserva siempre todas las acciones, aunque no esté marcado.
      </p>
      <div className="form-grid">
        {CAPABILITIES.map((capability) => (
          <fieldset key={capability} className="fld">
            <legend className="fld-lab">{CAPABILITY_LABELS[capability]}</legend>
            <div className="chk-row">
              {roles.map((role) => (
                <label key={role.id} className="chk">
                  <input
                    type="checkbox"
                    checked={rules[capability].includes(role.id)}
                    disabled={busy}
                    onChange={() => toggle(capability, role.id)}
                  />
                  <span>{role.name}</span>
                </label>
              ))}
            </div>
            {rules[capability].length === 0 && <span className="fld-hint">Según el rol</span>}
          </fieldset>
        ))}
      </div>
      {error !== null && (
        <p className="fld-err" role="alert" style={{ marginTop: 12 }}>
          {error}
        </p>
      )}
    </ModalShell>
  );
}
